import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Select, FormControl,
  FormLabel, Box, Text, Badge, useToast, Flex, IconButton,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import api from '../api';
import ConfirmModal from './ConfirmModal';

export default function BankAccountsModal({ isOpen, onClose, companyId = null, isOwn = false, title }) {
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showCurrency, setShowCurrency] = useState(false);
  const [currForm, setCurrForm] = useState({ code: '', name: '', symbol: '' });
  const toast = useToast();
  const [confirmDel, setConfirmDel] = useState(null);

  const resetForm = () => setForm({
    account_name:'', bank_name:'', account_type:'bank_account', currency_id:'', sort_code:'', account_number:'',
    branch:'', iban:'', swift_bic:'', account_holder:'', notes:'', is_default:false, external_id:'', company_id: companyId || '',
    cash_liability_sales_percent:'', cash_liability_purchase_percent:'',
    fee_bank_account_id:'', fee_nominal_code:'', fee_percent:'',
    auto_quote_project_id:'', auto_quote_task_id:'', auto_quote_description:'', auto_quote_nominal_code:'',
  });

  const fetchData = useCallback(async () => {
    try {
      const params = isOwn ? 'is_own=true' : `company_id=${companyId}`;
      const { data } = await api.get(`/bank-accounts?${params}`);
      setAccounts(data);
    } catch(e) { setAccounts([]); }
    try {
      const { data } = await api.get('/bank-accounts/currencies');
      setCurrencies(data);
    } catch(e) {}
    if (isOwn) {
      try { const { data } = await api.get('/companies'); setCompanies(Array.isArray(data) ? data : []); } catch(e) {}
      // Load projects for auto-quote picker
      try { const { data } = await api.get('/projects'); setProjects(Array.isArray(data) ? data : []); } catch(e) {}
    }
  }, [companyId, isOwn]);

  // Load tasks when a project is chosen for auto-quote
  const loadAutoQuoteTasks = async (projectId) => {
    if (!projectId) { setProjectTasks([]); return; }
    try {
      const { data } = await api.get(`/tasks?project_id=${projectId}`);
      setProjectTasks(Array.isArray(data) ? data.filter(t => !t.deleted_at) : []);
    } catch(e) { setProjectTasks([]); }
  };

  useEffect(() => { if (isOpen) { fetchData(); setShowForm(false); setEditingId(null); setShowCurrency(false); } }, [isOpen, fetchData]);

  const handleSave = async () => {
    if (!form.bank_name) { toast({ title:'Bank name required', status:'warning', duration:2000 }); return; }
    setSaving(true);
    try {
      const payload = { ...form, is_own: isOwn };
      if (!payload.company_id) payload.company_id = companyId || null;
      if (editingId) { await api.put(`/bank-accounts/${editingId}`, payload); }
      else { await api.post('/bank-accounts', payload); }
      await fetchData(); setShowForm(false); setEditingId(null); resetForm();
      toast({ title:'Saved', status:'success', duration:1500 });
    } catch(e) {
      // Show the real server message — "Failed" alone hides DB-level issues
      // like FK violations or strict-mode type errors that are easy to fix
      // once visible.
      const msg = e.response?.data?.error || e.message || 'Failed';
      toast({ title: msg, status: 'error', duration: 4000 });
    }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => setConfirmDel(id);
  const doDelete = async () => { await api.delete(`/bank-accounts/${confirmDel}`); await fetchData(); setConfirmDel(null); };

  const openEdit = (a) => {
    const pid = a.auto_quote_project_id || '';
    setForm({
      account_name:a.account_name||'', bank_name:a.bank_name, account_type:a.account_type||'bank_account',
      currency_id:a.currency_id||'', sort_code:a.sort_code||'', account_number:a.account_number||'',
      branch:a.branch||'', iban:a.iban||'', swift_bic:a.swift_bic||'',
      account_holder:a.account_holder||'', notes:a.notes||'', is_default:a.is_default||false,
      external_id:a.external_id||'', company_id:a.company_id||'',
      cash_liability_sales_percent: a.cash_liability_sales_percent||'',
      cash_liability_purchase_percent: a.cash_liability_purchase_percent||'',
      fee_bank_account_id: a.fee_bank_account_id||'',
      fee_nominal_code: a.fee_nominal_code||'', fee_percent: a.fee_percent||'',
      auto_quote_project_id: pid, auto_quote_task_id: a.auto_quote_task_id||'',
      auto_quote_description: a.auto_quote_description||'', auto_quote_nominal_code: a.auto_quote_nominal_code||'',
    });
    if (pid) loadAutoQuoteTasks(pid);
    setEditingId(a.id); setShowForm(true);
  };

  const f = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  const addCurrencyInline = async () => {
    if (!currForm.code || !currForm.name) { toast({ title: 'Code and name required', status: 'warning', duration: 2000 }); return; }
    try {
      await api.post('/bank-accounts/currencies', currForm);
      await fetchData();
      setCurrForm({ code: '', name: '', symbol: '' });
      setShowCurrency(false);
      toast({ title: 'Currency added', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>
            {title || (isOwn ? 'Our Bank Accounts' : 'Bank Accounts')}
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
              onClick={() => { resetForm(); setEditingId(null); setShowForm(!showForm); }}>{showForm ? 'Cancel' : 'Add'}</Button>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={2}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Account Name</FormLabel>
                    <Input size="xs" value={form.account_name} onChange={f('account_name')} placeholder="e.g. Main Business Account" /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Account Type</FormLabel>
                    <Select size="xs" value={form.account_type} onChange={e => setForm(p => ({ ...p, account_type: e.target.value }))}>
                      <option value="bank_account">🏦 Bank Account (Transfers, DD, SO)</option>
                      <option value="card_payment">💳 Card Payment</option>
                      <option value="cash_payment">💵 Cash Payment</option>
                      <option value="barter">🤝 Barter</option>
                      <option value="contra_value">⚖️ Contra Value</option>
                    </Select>
                  </FormControl>
                  {isOwn && (
                    <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Company</FormLabel>
                      <Select size="xs" value={form.company_id} onChange={e => setForm(p => ({ ...p, company_id: e.target.value }))}>
                        <option value="">— Select company —</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </Select></FormControl>
                  )}
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Bank Name *</FormLabel>
                    <Input size="xs" value={form.bank_name} onChange={f('bank_name')} placeholder="e.g. Lloyds" /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Currency</FormLabel>
                    <HStack><Select size="xs" value={form.currency_id} onChange={e => setForm(p => ({ ...p, currency_id: e.target.value ? parseInt(e.target.value) : null }))}>
                      <option value="">—</option>
                      {currencies.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                    </Select><IconButton icon={<FiPlus />} size="xs" variant="ghost" onClick={() => setShowCurrency(!showCurrency)} aria-label="Add" /></HStack>
                  </FormControl>
                </HStack>
                {showCurrency && (
                  <Box bg="green.50" p={2} borderRadius="md" w="100%">
                    <HStack spacing={2}>
                      <Input size="xs" placeholder="Code" value={currForm.code} onChange={e => setCurrForm(p => ({ ...p, code: e.target.value }))} w="60px" />
                      <Input size="xs" placeholder="Name" value={currForm.name} onChange={e => setCurrForm(p => ({ ...p, name: e.target.value }))} flex={1} />
                      <Input size="xs" placeholder="Symbol" value={currForm.symbol} onChange={e => setCurrForm(p => ({ ...p, symbol: e.target.value }))} w="40px" />
                      <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }} onClick={addCurrencyInline}>Add</Button>
                    </HStack>
                  </Box>
                )}
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Sort Code</FormLabel>
                    <Input size="xs" value={form.sort_code} onChange={f('sort_code')} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Account Number</FormLabel>
                    <Input size="xs" value={form.account_number} onChange={f('account_number')} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Branch</FormLabel>
                    <Input size="xs" value={form.branch} onChange={f('branch')} /></FormControl>
                </HStack>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>IBAN</FormLabel>
                    <Input size="xs" value={form.iban} onChange={f('iban')} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>SWIFT/BIC</FormLabel>
                    <Input size="xs" value={form.swift_bic} onChange={f('swift_bic')} /></FormControl>
                </HStack>
                <FormControl><FormLabel fontSize="xs" mb={0.5}>Account Holder</FormLabel>
                  <Input size="xs" value={form.account_holder} onChange={f('account_holder')} /></FormControl>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Notes</FormLabel>
                    <Input size="xs" value={form.notes} onChange={f('notes')} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>External ID</FormLabel>
                    <Input size="xs" value={form.external_id} onChange={f('external_id')} placeholder="e.g. Sage ref" /></FormControl>
                </HStack>

                {/* ── Barter configuration ── */}
                {form.account_type === 'barter' && (
                  <Box bg="teal.50" p={3} borderRadius="md" border="1px" borderColor="teal.200" w="100%">
                    <Text fontSize="9px" fontWeight="700" color="teal.700" textTransform="uppercase" letterSpacing="wide" mb={3}>Barter Account Configuration</Text>
                    <VStack spacing={3}>

                      {/* Cash liabilities */}
                      <Box w="100%" bg="white" p={2} borderRadius="md" border="1px" borderColor="teal.100">
                        <Text fontSize="9px" fontWeight="700" color="teal.600" textTransform="uppercase" mb={2}>£ GBP Cash Liability (VAT always calculated on GBP values)</Text>
                        <HStack spacing={3}>
                          <FormControl flex={1}>
                            <FormLabel fontSize="xs" mb={0.5}>% Cash Liability on Sales</FormLabel>
                            <Input size="xs" type="number" step="0.01" value={form.cash_liability_sales_percent}
                              onChange={f('cash_liability_sales_percent')} placeholder="e.g. 20" />
                            <Text fontSize="9px" color="gray.400">% of barter sales value → £ GBP liability</Text>
                          </FormControl>
                          <FormControl flex={1}>
                            <FormLabel fontSize="xs" mb={0.5}>% Cash Liability on Purchases</FormLabel>
                            <Input size="xs" type="number" step="0.01" value={form.cash_liability_purchase_percent}
                              onChange={f('cash_liability_purchase_percent')} placeholder="e.g. 20" />
                            <Text fontSize="9px" color="gray.400">% of barter purchase value → £ GBP liability</Text>
                          </FormControl>
                        </HStack>
                      </Box>

                      {/* Account fees */}
                      <Box w="100%" bg="white" p={2} borderRadius="md" border="1px" borderColor="teal.100">
                        <Text fontSize="9px" fontWeight="700" color="teal.600" textTransform="uppercase" mb={2}>Account Fee (e.g. BBX trading fee)</Text>
                        <HStack spacing={3} mb={2}>
                          <FormControl flex={1}>
                            <FormLabel fontSize="xs" mb={0.5}>Auto Fee % per Transaction</FormLabel>
                            <Input size="xs" type="number" step="0.01" value={form.fee_percent}
                              onChange={f('fee_percent')} placeholder="e.g. 2.5" />
                            <Text fontSize="9px" color="gray.400">% of barter value posted as £ GBP fee</Text>
                          </FormControl>
                          <FormControl flex={1}>
                            <FormLabel fontSize="xs" mb={0.5}>Fee → GBP Bank Account</FormLabel>
                            <Select size="xs" value={form.fee_bank_account_id}
                              onChange={e => setForm(p => ({ ...p, fee_bank_account_id: e.target.value }))}>
                              <option value="">— None —</option>
                              {accounts.filter(a => a.id !== editingId && a.account_type !== 'barter').map(a => (
                                <option key={a.id} value={a.id}>{a.account_name || a.bank_name}</option>
                              ))}
                            </Select>
                          </FormControl>
                        </HStack>
                        <FormControl>
                          <FormLabel fontSize="xs" mb={0.5}>Fee Nominal Code</FormLabel>
                          <Input size="xs" value={form.fee_nominal_code} onChange={f('fee_nominal_code')} placeholder="e.g. 7901" />
                        </FormControl>
                      </Box>

                      {/* Auto-quote on payment */}
                      <Box w="100%" bg="white" p={2} borderRadius="md" border="1px" borderColor="teal.100">
                        <Text fontSize="9px" fontWeight="700" color="teal.600" textTransform="uppercase" mb={2}>Auto-create Fee Invoice on Payment</Text>
                        <Text fontSize="9px" color="gray.500" mb={2}>When a barter payment is processed to/from this account, automatically raise a fee invoice (e.g. BBX account contract or trading fees) against the chosen project/task.</Text>
                        <VStack spacing={2}>
                          <HStack spacing={3} w="100%">
                            <FormControl flex={1}>
                              <FormLabel fontSize="xs" mb={0.5}>Project</FormLabel>
                              <Select size="xs" value={form.auto_quote_project_id}
                                onChange={async e => {
                                  const pid = e.target.value;
                                  setForm(p => ({ ...p, auto_quote_project_id: pid, auto_quote_task_id: '' }));
                                  await loadAutoQuoteTasks(pid);
                                }}>
                                <option value="">— No auto-invoice —</option>
                                {projects.filter(p => !p.deleted_at).map(p => (
                                  <option key={p.id} value={p.id}>{p.project_name}{p.company_name ? ` (${p.company_name})` : ''}</option>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl flex={1}>
                              <FormLabel fontSize="xs" mb={0.5}>Task</FormLabel>
                              <Select size="xs" value={form.auto_quote_task_id}
                                onChange={e => setForm(p => ({ ...p, auto_quote_task_id: e.target.value }))}
                                isDisabled={!form.auto_quote_project_id}>
                                <option value="">— choose task —</option>
                                {projectTasks.map(t => (
                                  <option key={t.id} value={t.id}>{t.task_title || `Task #${t.id}`}</option>
                                ))}
                              </Select>
                            </FormControl>
                          </HStack>
                          <HStack spacing={3} w="100%">
                            <FormControl flex={2}>
                              <FormLabel fontSize="xs" mb={0.5}>Fee Invoice Line Description</FormLabel>
                              <Input size="xs" value={form.auto_quote_description} onChange={f('auto_quote_description')}
                                placeholder="e.g. BBX trading fee" />
                            </FormControl>
                            <FormControl flex={1}>
                              <FormLabel fontSize="xs" mb={0.5}>Nominal Code</FormLabel>
                              <Input size="xs" value={form.auto_quote_nominal_code} onChange={f('auto_quote_nominal_code')}
                                placeholder="e.g. 7902" />
                            </FormControl>
                          </HStack>
                        </VStack>
                      </Box>

                    </VStack>
                  </Box>
                )}

                <Button size="xs" bg="brand.600" color="white" _hover={{ bg:'brand.700' }}
                  onClick={handleSave} isLoading={saving} alignSelf="flex-end">{editingId ? 'Update' : 'Add Account'}</Button>
              </VStack>
            </Box>
          )}
          {accounts.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>No bank accounts yet.</Text>
          ) : (
            <VStack spacing={2} align="stretch">
              {accounts.map(a => (
                <Box key={a.id} p={3} border="1px" borderColor="gray.100" borderRadius="md">
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Flex align="center" gap={2}>
                        <Text fontSize="13px" fontWeight="600">{a.account_name || a.bank_name}</Text>
                        {a.account_name && <Text fontSize="10px" color="gray.500">({a.bank_name})</Text>}
                        {a.currency_code && <Badge fontSize="8px" colorScheme="blue">{a.currency_code}</Badge>}
                        {a.account_type === 'card_payment'   && <Badge fontSize="8px" colorScheme="purple">💳 Card</Badge>}
                        {a.account_type === 'cash_payment'   && <Badge fontSize="8px" colorScheme="yellow">💵 Cash</Badge>}
                        {a.account_type === 'barter'         && <Badge fontSize="8px" colorScheme="teal">🤝 Barter</Badge>}
                        {a.account_type === 'contra_value'   && <Badge fontSize="8px" colorScheme="orange">⚖️ Contra</Badge>}
                        {!!a.is_default && <Badge fontSize="8px" colorScheme="green">Default</Badge>}
                      </Flex>
                      <HStack fontSize="10px" color="gray.500" spacing={3} mt={0.5}>
                        {a.sort_code && <Text>Sort: {a.sort_code}</Text>}
                        {a.account_number && <Text>Acc: {a.account_number}</Text>}
                        {a.branch && <Text>Branch: {a.branch}</Text>}
                      </HStack>
                      {a.account_holder && <Text fontSize="10px" color="gray.500">{a.account_holder}</Text>}
                      {a.company_name && <Text fontSize="9px" color="blue.500">{a.company_name}</Text>}
                      {a.external_id && <Text fontSize="9px" color="gray.400">Ext ID: {a.external_id}</Text>}
                    </Box>
                    <HStack spacing={0}>
                      <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openEdit(a)} aria-label="Edit" />
                      <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDelete(a.id)} aria-label="Delete" />
                    </HStack>
                  </Flex>
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
    <ConfirmModal isOpen={!!confirmDel} onClose={() => setConfirmDel(null)}
      title="Remove Bank Account" message="Are you sure you want to remove this bank account?"
      confirmLabel="Remove" confirmColor="red" onConfirm={doDelete} />
    </>
  );
}
